import { cn } from '../../../lib/utils';
import React, { useMemo } from 'react';
import DataTable from '../../common/DataTable';

export const COLUMN_CONFIG = [
  { id: 'Submission ID',    label: 'Submission ID',    defaultVisible: true },
  { id: 'Submission Date',  label: 'Submission Date',  defaultVisible: true },
  { id: 'Status',           label: 'Status',           defaultVisible: true },
  { id: 'Plan No.',         label: 'Plan No.',         defaultVisible: true },
  { id: 'Sending Category', label: 'Sending Category', defaultVisible: true },
  { id: 'Sending From',     label: 'Sending From',     defaultVisible: true },
  { id: 'Document Name',    label: 'Document Name',    defaultVisible: true },
  { id: 'Submitted By',     label: 'Submitted By',     defaultVisible: true },
  { id: 'Scheduled At',     label: 'Scheduled At',     defaultVisible: true },
  { id: 'Reminder',         label: 'Reminder Status',  defaultVisible: true },
  { id: 'actions',          label: 'Actions',          defaultVisible: true },
];

const SubmissionsTable = ({
  data = [],
  tableData = { headers: [] },
  title = '',
  loading = false,
  statusFilter = '',
  isDark = false,
}) => {

  const getStatusClasses = (status) => {
    const statusLower = status?.toLowerCase();
    const statusMap = {
      complete: 'bg-[#d9fbe6] text-[#0a6e29]',
      pending: 'bg-[#fff9db] text-[#9a7b00]',
      cancelled: 'bg-[#ffe3e3] text-[#b42318]',
      issues: 'bg-[#f9e3ff] text-purple-700',
      error: 'bg-[#ffe0cc] text-orange-700',
      scheduled: 'bg-[#dae3ff] text-[#3e62e2]',
      processing: 'bg-[#e5e7eb] text-[#4b5563]',
    };
    return statusMap[statusLower] || 'bg-gray-100 text-gray-700';
  };

  const mutedTextClass = `font-medium text-[16px] ${isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'}`;
  const dateTextClass  = `font-medium text-[16px] ${isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'}`;

  const allColumns = useMemo(
    () => [
      {
        id: 'Submission ID',
        accessorKey: 'id',
        header: 'Submission ID',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Submission Date',
        accessorKey: 'Submission Date',
        header: 'Submission Date',
        cell: ({ getValue }) => (
          <span className={dateTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Status',
        accessorKey: 'Status',
        header: 'Status',
        cell: ({ getValue, row }) => {
          const status = getValue();
          return (
            <div className={cn("flex flex-col items-start gap-[4px]")}>
              <span
                className={cn(
                  getStatusClasses(status),
                  "px-[16px] py-[6px] rounded-[78px] text-[13px] font-medium inline-flex items-center justify-center whitespace-nowrap capitalize w-[120px]"
                )}
              >
                {status}
              </span>
              {row.original.reassigned_at && (
                <span className={cn(
                  "inline-flex items-center gap-[5px] text-[12px] font-bold whitespace-nowrap",
                  isDark ? 'text-white' : 'text-[#072837]'
                )}>
                  <div
                    className={cn("w-[13px] h-[13px] shrink-0")}
                    style={{
                      backgroundColor: isDark ? '#ffffff' : '#072837',
                      WebkitMaskImage: "url('/assets/icons/reassigned.svg')",
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskSize: 'contain',
                      WebkitMaskPosition: 'center',
                      maskImage: "url('/assets/icons/reassigned.svg')",
                      maskRepeat: 'no-repeat',
                      maskSize: 'contain',
                      maskPosition: 'center',
                    }}
                  />
                  Last reassigned: {new Date(row.original.reassigned_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase().replace(',', ' ·')}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'Plan No.',
        accessorFn: (row) => row['Plan No.'],
        header: 'Plan No.',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Sending Category',
        accessorKey: 'Sending Category',
        header: 'Sending Category',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Sending From',
        accessorKey: 'Sending From',
        header: 'Sending From',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Document Name',
        accessorKey: 'Document Name',
        header: 'Document Name',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Submitted By',
        accessorKey: 'Submitted By',
        header: 'Submitted By',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Scheduled At',
        accessorKey: 'Scheduled At',
        header: 'Scheduled At',
        cell: ({ getValue }) => (
          <span className={mutedTextClass}>{getValue()}</span>
        ),
      },
      {
        id: 'Reminder',
        accessorKey: 'Reminder',
        header: 'Reminder Status',
        cell: ({ getValue }) => getValue(),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className={cn("flex items-center justify-end gap-[8px]")}>
            {row.original.Actions}
          </div>
        ),
      },
    ],
    [mutedTextClass, dateTextClass, isDark]
  );

  return (
    <DataTable
      data={data}
      columns={allColumns}
      columnConfig={COLUMN_CONFIG}
      storageKey="submissions_visible_columns"
      title={title}
      loading={loading}
      isDark={isDark}
      emptyStateText="No submissions found matching the current filters"
    />
  );
};

export default SubmissionsTable;
